<template>
  <b-overlay :show="isProcessing">
    <recorder
      ref="speechRecorder"
      :is-stop="isStop"
      :msg="msg"
      :info="info"
      :browser-support="browserSupport"
      class="p-1"
      :hide-player="true"
      @start="record"
      @stop="stop"
      @input="
        audio = $event.audio;
        audioUrl = $event.url;
      "
      @deleted="
        () => {
          msg = '';
          $emit('deleted');
        }
      "
      @handleBlob="(value) => $emit('handleBlob', value)"
      @recordingState="(value) => (isPlaying = value)"
    />
    <slot :is-playing="isPlaying"
          :stop-function="updateStop"
    />
    <b-modal
      v-if="!browserSupport"
      :visible="true"
      title="Unsupported Browser"
      no-close-on-backdrop
      centered
      ok-only
    >
      <h4 class="text-success">
        We apologize for any inconvenience, but it seems that the current
        browser you are using does not support speech recognition. To utilize
        this feature, we kindly request you to consider using a different
        browser. For an optimal experience, we recommend using either
        <strong class="text-danger">Chrome</strong> or <strong class="text-danger">Edge</strong>, as they provide
        support for speech recognition. Thank you for your understanding.
      </h4>

      <div v-if="isProblemConversionAllowed">
        <h4 class="mt-2 text-success">
          You can continue by converting the speech to writing problems:
        </h4>
        <b-form-checkbox
          v-model="problemConversion"
          switch
        >
          Speech to Writing Conversion
        </b-form-checkbox>
      </div>
    </b-modal>
  </b-overlay>
</template>

<script>
import { BModal, BFormCheckbox, BOverlay } from 'bootstrap-vue'
import useJwt from '@/auth/jwt/useJwt'
import { mapActions } from 'vuex'
import Recorder from './SpeechRecorder.vue'

export default {
  components: {
    Recorder,
    BModal,
    BFormCheckbox,
    BOverlay,
  },
  props: {
    autoStop: {
      type: Boolean,
      default: true,
    },
    hidePlayer: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    SpeechRecognition: null,
    SpeechGrammarList: null,
    SpeechRecognitionEvent: null,
    recognition: null,
    msg: '',
    oldMsg: '',
    audio: null,
    audioUrl: null,
    isStop: false,
    isPlaying: false,
    info: null,
    browserSupport: true,
    isProcessing: false,
    replaySpeechInterval: [],
  }),
  computed: {
    isProblemConversionAllowed: {
      get() {
        return this.$store.state.problem.isProblemConversionAllowed
      },
      set(value) {
        this.setIsProblemConversionAllowed(value)
      },
    },
    serverCheck() {
      return true
      // return !!this.$store.state.session.settings?.serverSpeechCheck
    },
    problemConversion: {
      get() {
        return this.$store.state.problem.problemConversion
      },
      set(value) {
        this.setProblemConversion(value)
      },
    },
  },
  watch: {
    serverCheck() {
      this.initializeSpeechRecognition()
    },
  },
  mounted() {
    this.initializeSpeechRecognition()
    // this.$root.$on('forceStopRecording', this.stop)
  },
  methods: {
    ...mapActions('problem', ['setIsProblemConversionAllowed', 'setProblemConversion']),
    initializeSpeechRecognition() {
      this.SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition
      if (!window.SpeechRecognition) {
        if (this.doBrowserReallyDontHaveSpeechSupport()) {
          // browser doesn't support SpeechRecognition API
          this.browserSupport = this.serverCheck
          useJwt.getProblemConversionSetting().then(response => {
            this.isProblemConversionAllowed = Number(response.data.data?.value) ?? false
          })
        }
      }
      if (this.browserSupport) {
        this.recognition = new this.SpeechRecognition()
        this.SpeechGrammarList = window.SpeechGrammarList || window.webkitSpeechGrammarList
        this.SpeechRecognitionEvent = window.SpeechRecognitionEvent || window.webkitSpeechRecognitionEvent
        const speechRecognitionList = new this.SpeechGrammarList()
        speechRecognitionList.addFromString(this.grammar, 1)
        this.recognition.grammars = speechRecognitionList
        this.recognition.continuous = false
        this.recognition.lang = 'en-US'
        this.recognition.interimResults = true
        this.recognition.maxAlternatives = 1
      }
    },
    record() {
      this.recognition.lang = 'en-US'
      this.recognition.start()
      this.info = 'Please speak to record your response'
      this.isStop = false
      this.isPlaying = true
      this.recognition.onresult = event => {
        const msg = event.results[0][0].transcript
        this.msg = `${this.oldMsg} ${msg}`
        if (this.msg) {
          this.info = 'Please speak to record your response'
        }
      }
      this.recognition.onspeechend = () => {
        if (this.autoStop) {
          this.isStop = true
        } else if (!this.isStop) {
          setTimeout(() => {
            if (!this.isStop) {
              this.recognition.stop()
              this.oldMsg = this.msg
              this.record()
            }
          }, 500)
        }
      }
    },
    stop() {
      this.isStop = true
      this.recognition.stop()
      this.$emit('is-processing')
      setTimeout(() => {
        this.$emit('text', this.msg)
        this.$emit('audio', this.audio)
        if (this.serverCheck) {
          if (!this.isProcessing) {
            this.isProcessing = true
            // const self = { ...this }
            const record = {
              text: this.msg,
              audio: this.audioUrl,
              audioFile: this.audio,
            }
            this.oldMsg = ''
            const file = new File([this.audio], 'audio-file.wav', { type: this.audio.type })
            const formData = new FormData()
            formData.append('audio', file)
            // eslint-disable-next-line no-unused-vars
            useJwt.speechToTextConversion(formData).then(response => {
              this.$emit('textAudio', {
                text: response.data.data.text,
                audio: record.audio,
                audioFile: record.audioFile,
              })
            }).finally(() => {
              this.isProcessing = false
            })
          }
        } else {
          this.$emit('textAudio', {
            text: this.msg,
            audio: this.audioUrl,
            audioFile: this.audio,
          })
          this.oldMsg = ''
        }
      }, 1000)
    },
    stopAllInterval() {
      this.replaySpeechInterval.forEach(item => {
        clearTimeout(item)
      })
      this.replaySpeechInterval = []
    },
    updateStop() {
      this.stopAllInterval()
      this.isStop = true
      this.isPlaying = false
      this.recognition.stop()
      setTimeout(() => {
        // eslint-disable-next-line no-unused-expressions
        this.$refs.speechRecorder?.deleteAudio()
      }, 100)
    },
  },
}
</script>
